import React, {useCallback, useEffect, useRef, useState} from 'react';
import {AppState, Platform, ScrollView, View} from 'react-native';
import * as Keychain from 'react-native-keychain';
import {EdgeInsets} from 'react-native-safe-area-context';
import {useSelector} from 'react-redux';
import {get, noop} from 'lodash';

import Button from '/component/base/Button';
import Checkbox from '/component/base/Checkbox/Checkbox';
import FormField from '/component/base/FormField';
import Icon from '/component/base/Icon';
import LoadingIndicator from '/component/base/LoadingIndicator';
import MessageBanner from '/component/base/MessageBanner';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import Gutter from '/component/layout/Gutter';
import {BiometricsAndPinSection} from '/component/partial/BiometricsAndPinSection';
import {MaintenanceBanner} from '/component/partial/MaintenanceBanner';
import {
  getButtonData,
  handleAutoBiometricsLogin,
  handleLoginFormErrorMessage,
  handleOnLoginChange,
  handlePasswordChange,
} from '/component/screen/Login/LoginForm/LoginForm.util';
import {Login} from '/constant/TestIDs';
import {LOGIN_TYPE} from '/enum/biometrics.enum';
import {
  BIOMETRIC_REQUESTED,
  HAS_OPTED_INTO_BIOMETRICS,
  HAS_SETUP_PIN,
  USER_PIN,
} from '/enum/storage.enum';
import {
  EVENT_NAMES,
  FORGOT_PASSWORD_ERROR_STATE,
  LOGIN_ENABLE,
  LOGIN_STATUS,
} from '/enum/tealium.enum';
import {withPopup} from '/provider/PopupProvider';
import {withSafeArea} from '/provider/SafeAreaProvider';
import {withTranslation} from '/provider/TranslationProvider';
import {variablesMaintenance} from '/redux/selector/remoteConfig.selector';
import {useStorage} from '/util/async-storage/useStorage';
import {getBiometricsUtilDefaultInstance} from '/util/biometrics/biometrics.util';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {getAccessToken} from '/util/fetch.util';
import {withHOC} from '/util/hoc.util';
import {openURL} from '/util/linking.util';
import {getLoggedInNumber} from '/util/rate/rate.util';
import {Logger} from '/util/splunk/logger.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';
import testProperties from '/util/testing/testProperties';
import {getUserName} from '/util/user.util';

import ForgotPasswordModal from './ForgotPasswordModal';
import {Header} from './Header';
import {useKeyboardState} from './LoginForm.hook';
import {styles} from './LoginForm.style';
import {useLoginForm} from './useLoginForm';
interface LoginFormProps {
  ts: (key: string, defaultText?: string) => string;
  onSignIn: () => void;
  onBiometrics: () => void;
  muleTokenBiometric: () => void;
  onResetPassword: () => void;
  onShowForgotPassword: () => void;
  isNetworkConnected: boolean;
  isLoading: boolean;
  shouldAutoLogin: boolean;
  email: string | null;
  showPopup: () => void;
  hidePopup: () => void;
  safeAreaInsets: EdgeInsets;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  ts = withTranslation.tsDefaultProp,
  onSignIn = noop,
  onBiometrics = noop,
  muleTokenBiometric = noop,
  onResetPassword = noop,
  isNetworkConnected = true,
  isLoading = false,
  shouldAutoLogin = true,
  email = null,
  showPopup = noop,
  hidePopup = noop,
  safeAreaInsets = withSafeArea.safeAreaInsetsDefaultProp,
  lastPaidRank,
}: any) => {
  const {
    formError,
    fieldErrors,
    setValue,
    handleOnSubmit,
    clearErrors,
    values: {login, password},
    loginErrorMutable,
  } = useLoginForm({
    onSignIn,
    onBiometrics,
    muleTokenBiometric,
    onResetPassword,
    defaultEmailValue: email,
    showPopup,
    hidePopup,
    lastPaidRank,
  });
  const [isModalVisible, setModalVisible] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');

  const [emailError, setEmailError] = useState(false);
  useEffect(() => {
    const loadCredentials = async () => {
      try {
        const token = await getAccessToken();
        const loggedInNumber = await getLoggedInNumber();
        if (token || loggedInNumber > 0) {
          const userName = await getUserName();
          userName && setValue('login', userName);
          Logger.manualSplunkLog({
            loginType: 'biometrics',
            message: `calling load credentials for user with accessToken :${token} for user ${userName} with logged in number ${loggedInNumber} and biometricsOptIn value ${biometricsOptIn}`,
            severity: 'LOW',
          });
        } else {
          setValue('login', '');
          setValue('password', '');
          Logger.manualSplunkLog({
            loginType: 'manual',
            message: `No user data fresh log in empty username and password and biometricsOptIn value ${biometricsOptIn}`,
            severity: 'LOW',
          });
        }
      } catch (error) {}
    };

    loadCredentials();
  }, [setValue]);

  const onLoginChange = useCallback(
    handleOnLoginChange({clearErrors, setValue, setEmailError}),
    [clearErrors, setValue, setEmailError],
  );
  const sendForgotPasswordEvent =
    getTealiumUtilDefaultInstance().generateTrackEvent({
      eventName: EVENT_NAMES.forgotPasswordClick,
    });

  // Removed isManuallyTyping state as it was causing input issues

  const onPasswordChange = useCallback(
    handlePasswordChange({clearErrors, setValue}),
    [clearErrors, setValue],
  );

  const onResetPasswordHandler = useRef(() => {
    if (loginErrorMutable.current) {
      sendForgotPasswordEvent({
        eventName: EVENT_NAMES.forgotPasswordClick,
        error_state: FORGOT_PASSWORD_ERROR_STATE.true,
        login_enable: LOGIN_ENABLE.false,
        success: LOGIN_STATUS.fail,
        email: email,
      });
      loginErrorMutable.current = '';
    } else {
      sendForgotPasswordEvent({
        eventName: EVENT_NAMES.forgotPasswordClick,
        error_state: FORGOT_PASSWORD_ERROR_STATE.false,
        login_enable: LOGIN_ENABLE.false,
        success: LOGIN_STATUS.success,
        email: email,
      });
    }
    setModalVisible(true);
  });
  const {setValue: setBiometricsOptInValue, getValue: getBiometricsOptInValue} =
    useStorage({
      key: HAS_OPTED_INTO_BIOMETRICS,
      defaultValue: false,
    });
  const {getValue: getbiometricsWasRequested} = useStorage({
    key: BIOMETRIC_REQUESTED,
    defaultValue: false,
  });

  const {getValue: getPinOptInValue} = useStorage({
    key: HAS_SETUP_PIN,
    defaultValue: false,
  });
  const [biometricType, setBiometricType] = useState();
  const [biometricsOptIn, setBiometricsOptIn] = useState<boolean>();
  const [pinOptIn, setPinOptIn] = useState();
  const [isFirstLogin, setIsFirstLogin] = useState();

  const [biometricPromptShown, setBiometricPromptShown] = useState(false);

  const appState = useRef(AppState.currentState);
  const checkFaceIDStatus = useCallback(async () => {
    const isBiometricRequested = await getbiometricsWasRequested();
    if (!isBiometricRequested) {
      setBiometricsOptInValue(false);
    } else {
      try {
        const biometryType = await Keychain.getSupportedBiometryType();
        if (Platform.OS === 'ios') {
          setBiometricsOptInValue(
            biometryType === Keychain.BIOMETRY_TYPE.FACE_ID,
          );
        } else if (Platform.OS === 'android') {
          setBiometricsOptInValue(
            biometryType === Keychain.BIOMETRY_TYPE.FINGERPRINT,
          );
        } else {
          setBiometricsOptInValue(false);
        }
        setBiometricsOptIn(await getBiometricsOptInValue());
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error checking biometric status:', error);
      }
    }
  }, [setBiometricsOptInValue, getbiometricsWasRequested]);
  useEffect(() => {
    const subscription = AppState.addEventListener('change', nextAppState => {
      if (
        appState.current.match(/inactive|background/) &&
        nextAppState === 'active'
      ) {
        checkFaceIDStatus();
      }
      appState.current = nextAppState;
    });
    return () => {
      subscription.remove();
    };
  }, [biometricType, getBiometricsOptInValue, getPinOptInValue]);
  useEffect(() => {
    checkFaceIDStatus();
  }, []);

  useEffect(() => {
    getBiometricsOptInValue().then(val => {
      setBiometricsOptIn(val);
    });

    getbiometricsWasRequested().then(val => {
      setIsFirstLogin(val);
    });
    getBiometricsUtilDefaultInstance().getBiometricType({
      setBiometricType,
    });
    getPinOptInValue().then(val => setPinOptIn(val));
    if (
      biometricsOptIn &&
      biometricType &&
      shouldAutoLogin &&
      !biometricPromptShown
    ) {
      handleAutoBiometricsLogin({
        shouldAutoLogin,
        biometricsOptIn,
        buttonData,
        setValue,
        handleOnSubmit,
        showPopup,
        hidePopup,
      });
      setBiometricPromptShown(true);
    }
  }, [
    getBiometricsOptInValue,
    biometricType,
    getPinOptInValue,
    shouldAutoLogin,
    biometricPromptShown,
  ]);

  const [setUpFaceID, setsetUpFaceID] = useState(false);

  const checkBoxStyle = setUpFaceID
    ? styles.selectedIcon
    : styles.unSelectedIcon;
  const checkBoxIconName = setUpFaceID
    ? 'checkbox-selected'
    : 'checkbox-unselected';

  const {getValue: getUserPinValue} = useStorage({
    key: USER_PIN,
    defaultValue: false,
  });

  const renderForm = () => (
    <>
      <FormField
        testID={Login.loginUsernameInput}
        webID="okta-signin-username"
        hasError={Boolean(formError || fieldErrors.login || emailError)}
        accessibilityLabel={ts('emailAccessibilityLabel', 'Email, required')}
        icon="profile"
        value={login}
        hasBiometrics={biometricsOptIn}
        buttonData={buttonData}
        handleOnSubmit={handleOnSubmit}
        setValue={setValue}
        label={ts('emailLabel', 'Email (username)')}
        onChangeText={onLoginChange}
        autoCorrect={false}
        // For iOS 11+ set textContentType to username or password
        // to enable autofill of login details from the device keychain.
        textContentType="username"
        autoCompleteType="email"
        importantForAutofill="yes"
        keyboardType="email-address"
      />
      {!formError &&
        !fieldErrors.password &&
        (emailError || (fieldErrors.login && !login && password)) && (
          <View style={styles.alertView}>
            <Icon name="alert" style={styles.alertIcon} />
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              style={styles.emailError}
              testID={Login.loginEmailErrorMessage}>
              {handleLoginFormErrorMessage({
                fieldErrors,
                formError,
                emailError: true,
              })}
            </Text>
          </View>
        )}
      <FormField
        accessibilityLabel={ts(
          'passwordAccessibilityLabel',
          'Password, required',
        )}
        testID={Login.loginPasswordInput}
        webID="signin-input-password"
        hasError={Boolean(formError || fieldErrors.password)}
        icon="lock"
        value={password}
        hasShowSecureText
        autoCapitalize="none"
        autoCorrect={false}
        label={ts('passwordLabel', 'Password')}
        style={{base: styles.passwordField}}
        onChangeText={onPasswordChange}
        textContentType="password"
        importantForAutofill="yes"
        autoCompleteType="password"
      />
      {(formError ||
        emailError ||
        fieldErrors.password ||
        (!fieldErrors.login && !fieldErrors.password)) && (
        <Text style={styles.errorPassword} testID={Login.loginErrorMessage}>
          {handleLoginFormErrorMessage({
            fieldErrors,
            formError,
            emailError: false,
          })}
        </Text>
      )}
      {(isFirstLogin === false || biometricType === null) &&
      Platform.OS !== 'web' ? (
        <View
          style={styles.checkboxContainer}
          {...testProperties(Login.loginSetupBiometricsCheckbox, true)}>
          <Checkbox
            checked={setUpFaceID}
            rectangleCheckbox
            style={{icon: checkBoxStyle}}
            checkedIconName={checkBoxIconName}
            onChange={() => setsetUpFaceID(!setUpFaceID)}
          />
          {Platform.OS === 'ios' ? (
            <Text
              style={styles.setupText}
              isBold
              {...testProperties(Login.loginSetupBiometricsLabel)}>
              {ts('setupFaceID', 'Set up Face ID')}
            </Text>
          ) : (
            <Text
              style={styles.setupText}
              isBold
              {...testProperties(Login.loginSetupBiometricsLabel)}>
              {ts('setupFaceID', 'Set up Touch ID')}
            </Text>
          )}
        </View>
      ) : (
        ''
      )}
    </>
  );

  const buttonData = getButtonData({biometricType})();

  const renderButtons = () => (
    <>
      {confirmationMessage ? (
        <View style={styles.messageBanner}>
          <MessageBanner
            style={{icon: styles.infoIcon, text: styles.closeText}}
            verticalBanner={true}
            alertLevel={'sucess'}
            alertSucessBg={true}
            iconName={'info'}
            message={confirmationMessage}
            closeIconTestID={Login.resetEmailSentCloseButton}
            bannerClose={() => setConfirmationMessage('')}
          />
        </View>
      ) : null}
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <LoadingIndicator size="small" />
        </View>
      ) : (
        <Button
          testID={Login.loginLoginButton}
          webID="okta-signin-submit"
          disabled={!isNetworkConnected}
          style={{base: styles.button}}
          onPress={() =>
            handleOnSubmit(
              LOGIN_TYPE.manual,
              setUpFaceID,
              setValue,
              buttonData,
              setBiometricsOptIn,
              biometricsOptIn,
            )
          }>
          {ts('login', 'Log in')}
        </Button>
      )}
      <TouchableOpacity
        style={styles.forgotPasswordButton}
        onPress={onResetPasswordHandler.current}>
        <Text
          style={styles.forgotPasswordText}
          isBold
          testID={Login.loginForgotPasswordButton}>
          {ts('forgotPassword', 'Forgot password?')}
        </Text>
      </TouchableOpacity>
      <ForgotPasswordModal
        isModalVisible={isModalVisible}
        onResetPassword={onResetPassword}
        setModalVisible={setModalVisible}
        setConfirmationMessage={setConfirmationMessage}
      />

      <BiometricsAndPinSection
        showPopup={showPopup}
        hidePopup={hidePopup}
        handleOnSubmit={handleOnSubmit}
        setValue={setValue}
        pinOptIn={pinOptIn}
        getUserPinValue={getUserPinValue}
      />
    </>
  );

  const {isKeyboardShown} = useKeyboardState();

  const renderInfoButtons = () => {
    const infoButtonsStyle = isKeyboardShown ? {} : styles.infoButtonsBottom;

    return (
      <View style={[styles.infoButtonsContainer, infoButtonsStyle]}>
        <Button
          testID={Login.loginTermsLink}
          type="secondaryTextLink"
          onPress={() =>
            openURL('url', 'https://www.optavia.com/us/en/terms-conditions')
          }>
          {ts('termsAndConditions', 'Terms & Conditions')}
        </Button>
        <Text>{ts('and', 'and')}</Text>
        <Button
          testID={Login.loginPrivacyPolicyLink}
          type="secondaryTextLink"
          onPress={() =>
            openURL('url', 'https://www.optavia.com/us/en/privacy-policy')
          }>
          {ts('privacyPolicy', 'Privacy Policy')}
        </Button>
      </View>
    );
  };

  const maintenance = useSelector(variablesMaintenance);
  const startBannerDate = get(maintenance, 'warning.startWarningDate', null);
  const endBannerDate = get(maintenance, 'warning.endWarningDate', null);
  const dateUtil = getDateUtilDefaultInstance();
  const showMaintenanceBanner = dateUtil.isDateWithinRange(
    startBannerDate,
    endBannerDate,
  );
  return (
    <>
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={[
          styles.base,
          {paddingTop: (safeAreaInsets || {}).top},
        ]}>
        {!isKeyboardShown && <MaintenanceBanner maintenance={maintenance} />}
        <Gutter
          size={40}
          style={[
            styles.content,
            {
              paddingBottom: isKeyboardShown
                ? Platform.select({ios: 200, android: 0})
                : 0,
            },
          ]}>
          <Header
            isKeyboardShown={isKeyboardShown}
            showMaintenanceBanner={showMaintenanceBanner}
          />
          <Gutter style={styles.spacer} />
          {renderForm()}
          <Gutter style={styles.spacer} />
          {renderButtons()}
        </Gutter>
        {renderInfoButtons()}
      </ScrollView>
    </>
  );
};

export default withHOC(
  withSafeArea,
  withTranslation('screen.loginForm'),
  withPopup(),
)(React.memo(LoginForm));
